import React, { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Box,
    Rating,
    Alert,
    Snackbar,
    Chip,
    Divider,
    Paper,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { toast } from "react-toastify";
import Loader from "../component/Loader/Loader";
import { getAllFeedbackOfStudent, saveFeedbackResponse } from "../helper/feedback_helper";

const MyFeedback = () => {
    const [feedbackForms, setFeedbackForms] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [questionRatings, setQuestionRatings] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [originalAutoAnswers, setOriginalAutoAnswers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [editedFields, setEditedFields] = useState({});

    // Rating to word mapping
    const ratingToWord = {
        1: "Bad",
        2: "Ok",
        3: "Good",
        4: "Very Good",
        5: "Excellent"
    };

    // Rating colors for visual feedback
    const ratingColors = {
        1: { color: "error", bgColor: "#ffebee", textColor: "#c62828" },
        2: { color: "warning", bgColor: "#fff3e0", textColor: "#ef6c00" },
        3: { color: "info", bgColor: "#e3f2fd", textColor: "#1565c0" },
        4: { color: "primary", bgColor: "#e8f5e8", textColor: "#2e7d32" },
        5: { color: "success", bgColor: "#e8f5e9", textColor: "#2e7d32" }
    };

    const handleStartFeedback = (form) => {
        setSelectedFeedback(form);
        
        // Initialize ratings array with default values (3 stars)
        const initialRatings = form.questions.map(() => 3);
        setQuestionRatings(initialRatings);
        
        // Initialize answers based on ratings
        const initialAnswers = form.questions.map((_, index) => 
            ratingToWord[initialRatings[index]]
        );
        setAnswers(initialAnswers);
        setOriginalAutoAnswers(initialAnswers);
        setEditedFields({});
        setError("");
        setOpenModal(true);
    };

    const handleQuestionRatingChange = (index, newValue) => {
        const updatedRatings = [...questionRatings];
        updatedRatings[index] = newValue;
        setQuestionRatings(updatedRatings);

        // Update the answer for this question based on new rating
        const updatedAnswers = [...answers];
        const newWord = ratingToWord[newValue];
        updatedAnswers[index] = newWord;
        setAnswers(updatedAnswers);

        // Update original auto answers
        const updatedOriginal = [...originalAutoAnswers];
        updatedOriginal[index] = newWord;
        setOriginalAutoAnswers(updatedOriginal);

        // If this field was marked as edited, remove the edit status
        if (editedFields[index]) {
            const newEdited = { ...editedFields };
            delete newEdited[index];
            setEditedFields(newEdited);
        }

        // Clear any rating-related errors
        if (error.includes("rating")) setError("");
    };

    const handleAnswerChange = (index, value) => {
        const updated = [...answers];
        updated[index] = value;
        setAnswers(updated);
        
        // Track which fields have been manually edited
        const autoWord = ratingToWord[questionRatings[index]];
        if (value !== autoWord) {
            setEditedFields(prev => ({ ...prev, [index]: true }));
        } else {
            const newEdited = { ...editedFields };
            delete newEdited[index];
            setEditedFields(newEdited);
        }
        
        if (error.includes("answer all questions")) setError("");
    };

    const handleResetToAutoAnswer = (index) => {
        const autoWord = ratingToWord[questionRatings[index]];
        const updated = [...answers];
        updated[index] = autoWord;
        setAnswers(updated);
        
        // Remove from edited fields
        const newEdited = { ...editedFields };
        delete newEdited[index];
        setEditedFields(newEdited);
    };

    const handleResetAllToAutoAnswers = () => {
        const autoAnswers = questionRatings.map(rating => ratingToWord[rating]);
        setAnswers(autoAnswers);
        setOriginalAutoAnswers(autoAnswers);
        setEditedFields({});
        toast.success("All answers reset to default based on ratings!");
    };

    const validateForm = () => {
        // Check if all questions have ratings
        const hasEmptyRating = questionRatings.some(r => !r || r === 0);
        if (hasEmptyRating) {
            setError("Please rate all questions.");
            return false;
        }
        
        // Check if all answers are filled (either auto or manual)
        const emptyAnswers = answers.some(ans => !ans?.trim());
        if (emptyAnswers) {
            setError("Please provide answers for all questions.");
            return false;
        }
        
        return true;
    };

    const handleSubmitFeedback = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const studentId = JSON.parse(localStorage.getItem('student'))?.id;
            
            // Calculate overall rating as average of all question ratings
            const overallRating = Math.round(
                questionRatings.reduce((sum, r) => sum + r, 0) / questionRatings.length
            );

            const payload = {
                studentId: studentId,
                feedbackFormId: selectedFeedback.id,
                rating: overallRating,
                questionRatings: questionRatings,
                answers: answers,
                isAutoGenerated: Object.keys(editedFields).length === 0
            };

            // Submit feedback
            saveFeedbackResponse(payload)
                .then(data => {
                    if (data?.success) {
                        toast.success(data.message);
                    }
                })
                .catch(err => {
                    console.error("Feedback submission error:", err);
                })
                .finally(() => {
                    setOpenSuccessModal(true);
                    setOpenModal(false);
                    setSubmitting(false);
                    fetchFeedbackForms();
                });
            
        } catch (err) {
            console.error("Error in feedback submission:", err);
            setOpenSuccessModal(true);
            setOpenModal(false);
            setSubmitting(false);
            fetchFeedbackForms();
        }
    };

    const handleCloseSuccessModal = () => {
        setOpenSuccessModal(false);
    };

    const getAnswerStatus = (index) => {
        if (editedFields[index]) {
            return { 
                icon: <EditIcon fontSize="small" />, 
                text: "Custom answer",
                color: "primary"
            };
        } else {
            return { 
                icon: <AutoAwesomeIcon fontSize="small" />, 
                text: "Auto-generated",
                color: "success"
            };
        }
    };

    const getRatingInfo = (rating) => {
        return {
            word: ratingToWord[rating] || "Not Rated",
            color: ratingColors[rating]?.color || "default",
            bgColor: ratingColors[rating]?.bgColor || "#f5f5f5",
            textColor: ratingColors[rating]?.textColor || "#000000"
        };
    };

    const fetchFeedbackForms = async () => {
        try {
            setLoading(true);
            const id = JSON.parse(localStorage.getItem('student'))?.id;
            const data = await getAllFeedbackOfStudent(id);
            if (data?.success) {
                setFeedbackForms(data?.result || []);
            } else {
                toast.error(data.message || "Something went wrong");
            }
        } catch (err) {
            toast.error("Error fetching feedbacks: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Calculate progress
    const totalQuestions = selectedFeedback?.questions?.length || 0;
    const ratedQuestions = questionRatings.filter(r => r > 0).length;
    const customAnswersCount = Object.keys(editedFields).length;

    useEffect(() => {
        fetchFeedbackForms();
    }, []);

    return (
        <>
            {loading && <Loader />}

            <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Available Feedback Forms
                </Typography>

                {feedbackForms.length === 0 ? (
                    <Card sx={{ mt: 2, textAlign: 'center', p: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No feedback forms available at the moment.
                        </Typography>
                    </Card>
                ) : (
                    feedbackForms.map((form) => (
                        <Card key={form.id} sx={{ mb: 2, '&:hover': { boxShadow: 3 } }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {form.name}
                                </Typography>
                                {form.description && (
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        {form.description}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        <Chip 
                                            label={`${form.questions?.length || 0} Questions`} 
                                            size="small" 
                                            variant="outlined"
                                        />
                                        <Chip
                                            label="Rate each question"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Stack>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleStartFeedback(form)}
                                        disabled={submitting}
                                    >
                                        Start Feedback
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))
                )}

                {/* Feedback Submission Modal */}
                <Dialog 
                    open={openModal} 
                    onClose={() => !submitting && setOpenModal(false)} 
                    fullWidth 
                    maxWidth="md"
                    scroll="paper"
                >
                    <DialogTitle sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 2
                    }}>
                        <Box>
                            <Typography variant="h6">
                                {selectedFeedback?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Rate each question and provide feedback
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => !submitting && setOpenModal(false)}
                            disabled={submitting}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}
                        
                        {/* Progress Summary */}
                        {totalQuestions > 0 && (
                            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Progress Summary
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={`${ratedQuestions}/${totalQuestions} Rated`}
                                        color={ratedQuestions === totalQuestions ? "success" : "warning"}
                                        size="small"
                                    />
                                    <Chip
                                        label={`${customAnswersCount} Custom Answers`}
                                        color={customAnswersCount > 0 ? "primary" : "default"}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            </Paper>
                        )}

                        {/* Rating Guide */}
                        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f1f8e9' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Rating Guide:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {[1, 2, 3, 4, 5].map(star => (
                                    <Chip
                                        key={star}
                                        icon={<StarIcon />}
                                        label={`${star} Star = ${ratingToWord[star]}`}
                                        size="small"
                                        color={ratingColors[star].color}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Paper>

                        {/* Questions List */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Questions
                        </Typography>
                        
                        <Divider sx={{ mb: 3 }} />
                        
                        <Box>
                            {selectedFeedback?.questions?.map((question, index) => {
                                const ratingInfo = getRatingInfo(questionRatings[index]);
                                const status = getAnswerStatus(index);
                                const isEdited = editedFields[index];
                                
                                return (
                                    <Paper 
                                        key={index} 
                                        sx={{ 
                                            mb: 3, 
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: isEdited ? 'warning.light' : 'grey.200',
                                            bgcolor: isEdited ? '#fff9e6' : 'white',
                                            transition: 'all 0.2s'
                                        }}
                                        elevation={isEdited ? 2 : 1}
                                    >
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Q{index + 1}. {question}
                                            </Typography>
                                        </Box>
                                        
                                        {/* Rating for this question */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            mb: 2,
                                            p: 1.5,
                                            bgcolor: ratingInfo.bgColor,
                                            borderRadius: 1
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Your Rating:
                                                </Typography>
                                                <Rating
                                                    name={`rating-${index}`}
                                                    value={questionRatings[index]}
                                                    onChange={(event, newValue) => 
                                                        handleQuestionRatingChange(index, newValue)
                                                    }
                                                    size="medium"
                                                    disabled={submitting}
                                                />
                                            </Box>
                                            
                                            {questionRatings[index] > 0 && (
                                                <Chip
                                                    label={ratingInfo.word}
                                                    color={ratingInfo.color}
                                                    size="small"
                                                    icon={<StarIcon />}
                                                />
                                            )}
                                        </Box>
                                        
                                        {/* Answer Field */}
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                mb: 1,
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Your Answer:
                                                </Typography>
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        icon={status.icon}
                                                        label={status.text}
                                                        size="small"
                                                        color={status.color}
                                                        variant="outlined"
                                                    />
                                                    
                                                    {isEdited && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handleResetToAutoAnswer(index)}
                                                            disabled={submitting}
                                                            startIcon={<AutoAwesomeIcon />}
                                                        >
                                                            Reset
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                placeholder={questionRatings[index] > 0 
                                                    ? `Default: "${ratingToWord[questionRatings[index]]}" - You can type your own answer`
                                                    : "Please rate this question first"
                                                }
                                                value={answers[index] || ""}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                disabled={submitting || !questionRatings[index]}
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: isEdited ? '#fff9e6' : 'white',
                                                        fontWeight: isEdited ? 500 : 400
                                                    }
                                                }}
                                                InputProps={{
                                                    style: {
                                                        color: isEdited ? '#ed6c02' : 'inherit'
                                                    }
                                                }}
                                            />
                                            
                                            {!answers[index] && questionRatings[index] > 0 && (
                                                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                                                    Auto-generated answer will appear here
                                                </Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                        
                        {/* Global Actions */}
                        {customAnswersCount > 0 && (
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleResetAllToAutoAnswers}
                                    disabled={submitting}
                                    startIcon={<AutoAwesomeIcon />}
                                >
                                    Reset All to Default Answers
                                </Button>
                            </Box>
                        )}

                        {/* Summary Box */}
                        {totalQuestions > 0 && ratedQuestions === totalQuestions && (
                            <Paper sx={{ 
                                mt: 3, 
                                p: 2, 
                                bgcolor: '#e8f5e9',
                                textAlign: 'center'
                            }}>
                                <Typography variant="subtitle1" sx={{ color: '#2e7d32' }}>
                                    ✨ All questions rated! You can submit your feedback.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Overall Rating: {Math.round(questionRatings.reduce((sum, r) => sum + r, 0) / totalQuestions)} Stars
                                </Typography>
                            </Paper>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Button 
                            onClick={() => setOpenModal(false)} 
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleSubmitFeedback}
                            disabled={submitting || ratedQuestions !== totalQuestions}
                            color="primary"
                        >
                            {submitting ? "Submitting..." : "Submit Feedback"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success Modal */}
                <Dialog 
                    open={openSuccessModal} 
                    onClose={handleCloseSuccessModal}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon 
                            sx={{ 
                                fontSize: 60, 
                                color: 'success.main',
                                mb: 2
                            }} 
                        />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Thank You!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Your feedback has been submitted successfully.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            We appreciate your detailed feedback.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleCloseSuccessModal}
                            sx={{ mt: 2 }}
                        >
                            Close
                        </Button>
                    </DialogContent>
                </Dialog>

                {/* Global Error Snackbar */}
                <Snackbar
                    open={!!error && !openModal}
                    autoHideDuration={6000}
                    onClose={() => setError("")}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setError("")} severity="error" variant="filled">
                        {error}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default MyFeedback;
